import React from 'react';
import { UserEntity } from 'Models/Entities';
import { SecuredGroups } from 'Models/Security/UserGroups';
import { store } from 'Models/Store';
import { EditAccountPolicy } from 'Views/Components/_HumanWritten/AccessPermissions/Security';
import {
	getHighestRole,
	getUserGroupRolesOnly,
} from 'Views/Components/_HumanWritten/AccessPermissions/Types/UserGroupRoles';

// Derived from the shared security module (the same list SecuredPage gates staff pages on)
// so a new staff-level role can't silently misclassify users here.
const STAFF_GROUPS: string[] = SecuredGroups.create.onlyStaff().groups;

/**
 * Role-hierarchy gating for the staff user detail page, lifted from the legacy
 * AccountDetailsInnerWrap: whether the viewed account is itself staff-level (drives which tabs
 * exist) and whether the logged-in staff member outranks them enough to edit
 * (EditAccountPolicy).
 */
export function useCanEditUser(user: UserEntity) {
	const isTargetStaff = !!user.userGroups?.some(g => STAFF_GROUPS.includes(g.role?.name ?? ''));

	const canEdit = React.useMemo(() => {
		if (user.id === store.userId) return true;

		const currentUserRoles = getUserGroupRolesOnly(store.userGroups.map(g => g.name));
		const targetUserRoles = getUserGroupRolesOnly(user.userGroups?.map(g => g.role?.name ?? '') ?? []);

		if (!currentUserRoles.length || !targetUserRoles.length) return true;

		const currentHighest = getHighestRole(currentUserRoles);
		const targetHighest = getHighestRole(targetUserRoles);

		return EditAccountPolicy[currentHighest].includes(targetHighest);
	}, [user]);

	return { canEdit, isTargetStaff };
}
